import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Col, Modal, Row, Table, message } from "antd";
import React, { Fragment } from "react";
import { RouteComponentProps } from "react-router";
import { NavLink } from "react-router-dom";
import Card from "../components/Card";
import EntitledSection from "../components/EntitledSection";
import entitlements from "../config/entitlements";
import { AUMTarget } from "../helpers/shared-interfaces";
import { request } from "../helpers/utilities";
import { CURRENT_PERIOD_QUERY } from "../gql/general";
import { DELETE_AUM_TARGET_MUTATION, GET_AUM_TARGETS } from "../gql/aum";
import { PageToolbar } from "../components/PageToolbar";
import { generateDisplayErrorMessage } from "../helpers/utilities";


interface State {
  isLoading: boolean;
  aumTargets: AUMTarget[];
  showDialog: boolean;
  idToBeDeleted: string | null;
  currentPeriod: string[];
}

export type HOCProp = (id: string) => void;

interface AUMColumnProps {
  title: string;
  dataIndex: string[] | string;
  key: string;
  width: string;
  render?: (record: AUMTarget) => JSX.Element | null;
}

export const ColumnsCreatorHOC = function (
  toggleDeleteConfirmationModal: HOCProp,
): AUMColumnProps[] {
  return [
    {
      title: "Role",
      dataIndex: ["role", "name"],
      key: "Role",
      width: "40%",
    },
    {
      title: "Amount (GHS)",
      dataIndex: "amount",
      key: "Amount",
      width: "30%",
    },
    {
      title: "Actions",
      dataIndex: "",
      key: "Actions",
      width: "30%",
      render: function displayActions(record: AUMTarget): JSX.Element | null {
        return (
          <span>
            <EntitledSection entitlement={entitlements.AUMTargets.edit}>
              <NavLink to={`aum-targets/${record.id}/edit`}>
                <Button
                  icon={<EditOutlined />}
                  size="middle"
                  style={{
                    backgroundColor: "  rgb(29, 55, 100)",
                    color: "white",
                    marginRight: "10px",
                  }}
                >
                  Edit
                </Button>
              </NavLink>
            </EntitledSection>
            {record.role.users.length === 0 && (
              <EntitledSection entitlement={entitlements.AUMTargets.delete}>
                <Button
                  id="aum-target-delete-btn"
                  icon={<DeleteOutlined />}
                  size="middle"
                  onClick={(): void => {
                    toggleDeleteConfirmationModal(record.id);
                  }}
                >
                  Delete
                </Button>
              </EntitledSection>
            )}
          </span>
        );
      },
    },
  ];
};

export function AUMTargetsTableFactory(
  data: AUMTarget[],
  toggleDeleteConfirmationModal: HOCProp,
  isLoading: boolean,
): JSX.Element {
  const tempData: AUMTarget[] = data.map((aumtarget: AUMTarget): AUMTarget => {
    return { ...aumtarget, key: aumtarget.id };
  });
  return (
    <Table
              loading={isLoading}
      style={{ width: "100%" }}
      bordered={true}
      columns={ColumnsCreatorHOC(toggleDeleteConfirmationModal)}
      dataSource={tempData}
      pagination={false}
    />
  );
}

function ModalWrapper(
  showDialog: boolean,
  deleteRole: () => void,
  toggleDeleteConfirmationModal: () => void,
): JSX.Element {
  return (
    <Modal
      title="Confirm Deletion Action"
      visible={showDialog}
      {...{
        okText: "Yes",
        okType: "danger",
        cancelButtonProps: {
          className: "modal-cancel-btn",
        },
        okButtonProps: {
          className: "modal-confirm-btn",
        },
      }}
      onOk={deleteRole}
      onCancel={toggleDeleteConfirmationModal}
    >
      <div>Are you sure you want to delete this AUM-Target?</div>
    </Modal>
  );
}

export default class AumTargets extends React.Component<
  RouteComponentProps,
  State
> {
  _isMounted = false;
  constructor(props: Readonly<RouteComponentProps>) {
    super(props);
    this.state = {
      isLoading: true,
      aumTargets: [],
      showDialog: false,
      idToBeDeleted: null,
      currentPeriod: [],
    };

    this.toggleDeleteConfirmationModal =
      this.toggleDeleteConfirmationModal.bind(this);
    this.deleteAUMTarget = this.deleteAUMTarget.bind(this);
  }

  getAllAUMTargets = async (): Promise<void> => {
    try {
      const { data: { data } }: any = await request({
        query: GET_AUM_TARGETS,
      });
      this.setState({
        aumTargets: data.aumTargets,
      });
    } catch (error) {
      message.error(`Failed to load AUM Targets - ${generateDisplayErrorMessage(error)}`);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  getCurrentPeriod = async (): Promise<void> => {
    try {
      const { data: { data } }: any = await request({
        query: CURRENT_PERIOD_QUERY,
      });
      this.setState({
        currentPeriod: data.currentPeriod,
      });
    } catch (error) {
       // Fail silently
    }
  };

  deleteAUMTarget(): void {
    const { idToBeDeleted } = this.state;

    interface Response {
      data: {
        data: {
          deleteAumTarget: {
            result: boolean;
            errors: [{ property: string; description: string }];
          };
        };
      };
    }

    this.setState(
      {
        isLoading: true,
      },
      (): void => {
        request({
          query: DELETE_AUM_TARGET_MUTATION,
          variables: { id: idToBeDeleted },
        }).then(({ data }: Response): void => {
          this.setState({ isLoading: false, showDialog: false });
          if (
            data.data.deleteAumTarget.errors.length < 1 &&
            data.data.deleteAumTarget.result
          ) {
            message.success("AUM Target deleted successfuly");
            this.redirectToAllAUMTargetsPage();
          } else {
            this.setState({ isLoading: false, showDialog: false });
            message.error("Failed to delete AUM Target");
          }
        });
      },
    );
  }

  toggleDeleteConfirmationModal(id?: string): void {
    const prevState = this.state.showDialog;
    const finalId = id || "";
    this.setState({ showDialog: !prevState, idToBeDeleted: finalId });
  }

  redirectToNewAumTargetPage = (): void => {
    this.props.history.push("/aum-targets/new");
  };

  redirectToAllAUMTargetsPage = (): void => {
    this.props.history.push("/aum-targets");
  };

  async componentDidMount(): Promise<void> {
    this._isMounted = true;
    this.setState({ isLoading: true });
    await Promise.all([
      this.getAllAUMTargets(),
      this.getCurrentPeriod()
    ]);
  }

  componentWillUnmount(): void {
    this._isMounted = false;
  }

  render(): JSX.Element | null {
    const { aumTargets, showDialog, currentPeriod } = this.state;
    //test
    return (
        <Card>
          <Fragment>
            {ModalWrapper(
              showDialog,
              this.deleteAUMTarget,
              this.toggleDeleteConfirmationModal,
            )}

            <PageToolbar modelName="AUM targets"
              actions={
                currentPeriod.length > 0 && (
                  <span
                    style={{
                      fontWeight: "bold",
                      fontSize: "20px",
                      marginRight: "16px",
                      display: "flex",
                      alignItems: "center"
                    }}
                  >
                    {currentPeriod[0]} - {currentPeriod[11]}
                  </span>
                )
              }
              primaryAction={
                <EntitledSection entitlement={entitlements.AUMTargets.create}>
                  <Button
                    type="primary"
                    onClick={this.redirectToNewAumTargetPage}
                    data-testid="create-aumtarget-button"
                  >
                    <PlusOutlined />
                    Create AUM-Target
                  </Button>
                </EntitledSection>
              }
              totalCount={aumTargets.length}
              currentDataCount={aumTargets.length}
              page={1}
              pageSize={aumTargets.length > 0 ? aumTargets.length : 10}
            />
            <Row
              style={{
                marginBottom: "18px",
              }}
            >
              <Col span={24}>
                {AUMTargetsTableFactory(
                  aumTargets,
                  this.toggleDeleteConfirmationModal,
                  this.state.isLoading,
                )}
              </Col>
            </Row>
          </Fragment>
        </Card>
    );
  }
}
